import {useCallback, useEffect, useState} from "react";
import {Button, Checkbox, Input, Typography, Radio, Skeleton, RadioChangeEvent, message} from "antd";
import {LocationDto} from "../../../../generated/dashboard-client";
import MembersApi from "../../../../api/MembersApi";
import {error, success} from "../../../../service/MessageService";
import AccountsApi from "../../../../api/AccountsApi";
import {handleServerError} from "../../../../util/ErrorHandler";
import {useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {env} from "../../../../env/env";
import {useRecoilState} from "recoil";
import {memberState} from "../../../../state/atoms";
import {LeftOutlined} from "@ant-design/icons";
import {MemberLocationPermissionDto} from "../../../../generated/dashboard-client";
import {
  AddOn,
  AccessLevel,
  addOnNote,
  addOnsFor,
  clearInvalidAddOns,
  isAddOnChecked,
  isAddOnDisabled,
} from "./permissionModel";

const {Title} = Typography;

let PERMISSIONS = {
  all: {
    viewOnly: false,
    downloadPublish: true,
    publish: false,
    createTemplate: true,
    updateDrd: false,
    orderPrint: true,
    manageBrand: false,
    itemLibraryOnly: false,
    dataOnly: false,
    holidayTemplates: false,
  }
} as any;

function getDefaultLocationPermission(locationId: number) {
  return {
    viewOnly: false,
    downloadPublish: true,
    publish: false,
    createTemplate: true,
    updateDrd: false,
    orderPrint: true,
    manageBrand: false,
    itemLibraryOnly: false,
    dataOnly: false,
    holidayTemplates: false,
    locationId
  }
}

export function UserLocationsPermissions() {
  const location = useLocation();
  const navigate = useNavigate();
  let {id} = useParams<{ id: string }>();

  const [searchParams] = useSearchParams();
  const activate = searchParams.get("activate");

  const [member] = useRecoilState(memberState);

  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("user" as "user" | "admin");
  const [step, setStep] = useState<"email" | "locations" | "permissions">("email");
  const [selLocations, setSelLocations] = useState<string[]>([]);
  const [locationMode, setLocationMode] = useState<"all" | "byLocation">("all");
  const [locationForPermission, setLocationForPermission] = useState(null as LocationDto | null);
  const [saving, setSaving] = useState(false);

  const [viewOnly, setViewOnly] = useState(false);
  const [itemLibraryOnly, setItemLibraryOnly] = useState(false);
  const [dataOnly, setDataOnly] = useState(false);
  const [downloadPublish, setDownloadPublish] = useState(false);
  const [publish, setPublish] = useState(false);
  const [createTemplate, setCreateTemplate] = useState(false);
  const [updateDrd, setUpdateDrd] = useState(false);
  const [orderPrint, setOrderPrint] = useState(true);
  const [manageBrand, setManageBrand] = useState(false);
  const [holidayTemplates, setHolidayTemplates] = useState(false);

  const [locations, setLocations] = useState([] as LocationDto[]);

  const [hasSelectedPermissions, setHasSelectedPermissions] = useState(false);

  const updatePermissionsForLocation = useCallback((location: any) => {
    if (!location) return;
    setViewOnly(location.viewOnly);
    setItemLibraryOnly(location.itemLibraryOnly ?? false);
    setDataOnly(location.dataOnly ?? false);
    setDownloadPublish(location.downloadPublish);
    setPublish(location.publish ?? false);
    setCreateTemplate(location.createTemplate);
    setUpdateDrd(location.updateDrd);
    setOrderPrint(location.orderPrint);
    setManageBrand(location.manageBrand ?? false);
    setHolidayTemplates(location.holidayTemplates ?? false);
  }, [setViewOnly, setDownloadPublish, setCreateTemplate, setUpdateDrd, setOrderPrint]);

  const selectLocationForPermissions = useCallback((location: LocationDto) => {
    if (!location) return;
    setLocationForPermission(location);
    if (!PERMISSIONS[location.id!]) {
      PERMISSIONS[location.id!] = getDefaultLocationPermission(location.internalId!);
    }

    updatePermissionsForLocation(PERMISSIONS[location.id!]);
  }, [setLocationForPermission, updatePermissionsForLocation]);

  useEffect(() => {
    let has = false;
    if (viewOnly || downloadPublish || createTemplate || itemLibraryOnly || dataOnly || manageBrand || orderPrint) {
      has = true;
    }
    if (member.diningRDIntegration && updateDrd) {
      has = true;
    }
    setHasSelectedPermissions(has);
  }, [viewOnly, downloadPublish, createTemplate, itemLibraryOnly, dataOnly, updateDrd, orderPrint, manageBrand, member.diningRDIntegration]);

  useEffect(() => {
    const path = location.pathname;
    if (path.indexOf("new") > -1) {
      setStep("email");
    } else if (path.indexOf("locations") > -1) {
      setStep("locations");
    } else if (path.indexOf("permissions") > -1) {
      setStep("permissions");
    }
  }, [location.pathname]);

  useEffect(() => {
    setLoading(true);
    AccountsApi.getUserLocationsLight(member.id).then(userLocations => {
      const locations = [...userLocations.data].sort((a, b) =>
        (a.name ?? "").localeCompare(b.name ?? "", undefined, {sensitivity: "base"})
      );
      setLocations(locations);

      if (!!id) {
        AccountsApi.getUsersDetails(member.id).then((resp) => {
          const found = resp.data.users?.find(it => it.id === id);
          if (found) {
            setEmail(found.email ?? "");
          }
        });
        MembersApi.getUserTeamLocationPermissions(id).then(resp => {
          const selected = [] as string[];
          if (locations.length > 0) {
            resp.data.forEach(locationPermissions => {
              const found = locations.find(it => it.internalId === locationPermissions.locationId);
              if (found && found.id) {
                updatePermissionCache(found.id, locationPermissions);
                selected.push(found.id);
              }
            });

            recomputeAllPermissions(selected);
            selectLocationForPermissions(locations.find(it => it.id === selected[0])!);
            setSelLocations(selected);
          }
        });
        setLocationMode("byLocation");
      } else {
        setViewOnly(false);
        setItemLibraryOnly(false);
        setDataOnly(false);
        setDownloadPublish(true);
        setCreateTemplate(true);
        setUpdateDrd(false);
        setOrderPrint(true);
        setHolidayTemplates(false);
      }

      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, [id, member.id, selectLocationForPermissions]);

  function handleEmailSubmit() {
    if (!email.trim()) return;
    MembersApi.validateUserInviteEmail(email.trim()).then(() => {
      if (role === "user") {
        setStep("locations");
      } else {
        inviteNewUser();
      }
    }).catch(() => {
      message.error("Email already in use");
    })
  }

  function updatePermissionCache(locationId: string, locationPermissions: MemberLocationPermissionDto) {
    PERMISSIONS[locationId] = {
      viewOnly: locationPermissions.viewOnly,
      itemLibraryOnly: locationPermissions.itemLibraryOnly,
      dataOnly: locationPermissions.dataOnly ?? false,
      downloadPublish: locationPermissions.downloadAndPublish,
      publish: locationPermissions.publish ?? false,
      createTemplate: locationPermissions.createTemplate,
      updateDrd: locationPermissions.updateDrd,
      orderPrint: locationPermissions.orderPrint,
      manageBrand: (locationPermissions as any).manageBrand ?? false,
      holidayTemplates: locationPermissions.holidayTemplates ?? false,
      locationId: locationPermissions.locationId,
    }
  }

  function recomputeAllPermissions(selectedIds: string[]) {
    const flags = ["viewOnly", "downloadPublish", "publish", "createTemplate", "updateDrd", "orderPrint", "manageBrand", "itemLibraryOnly", "dataOnly", "holidayTemplates"];
    const locs = selectedIds.map(it => PERMISSIONS[it]).filter(Boolean);
    const all = {} as any;
    flags.forEach(flag => {
      all[flag] = locs.length > 0 && locs.every(loc => !!loc[flag]);
    });
    PERMISSIONS["all"] = all;
  }

  function syncManageBrand(value: boolean) {
    setManageBrand(value);
    PERMISSIONS[(locationMode !== "all" && locationForPermission) ? locationForPermission.id! : "all"].manageBrand = value;
  }

  function syncItemLibraryOnly(value: boolean) {
    setItemLibraryOnly(value);
    PERMISSIONS[(locationMode !== "all" && locationForPermission) ? locationForPermission.id! : "all"].itemLibraryOnly = value;
  }

  function syncDataOnly(value: boolean) {
    setDataOnly(value);
    PERMISSIONS[(locationMode !== "all" && locationForPermission) ? locationForPermission.id! : "all"].dataOnly = value;
  }

  function syncCreateTemplates(value: boolean) {
    setCreateTemplate(value);
    PERMISSIONS[(locationMode !== "all" && locationForPermission) ? locationForPermission.id! : "all"].createTemplate = value;
  }

  function syncViewOnly(value: boolean) {
    setViewOnly(value);
    PERMISSIONS[(locationMode !== "all" && locationForPermission) ? locationForPermission.id! : "all"].viewOnly = value;
  }

  function syncDownloadAndPublish(value: boolean) {
    setDownloadPublish(value);
    PERMISSIONS[(locationMode !== "all" && locationForPermission) ? locationForPermission.id! : "all"].downloadPublish = value;
  }

  function syncPublish(value: boolean) {
    setPublish(value);
    PERMISSIONS[(locationMode !== "all" && locationForPermission) ? locationForPermission.id! : "all"].publish = value;
  }

  function syncUpdateDrd(value: boolean) {
    setUpdateDrd(value);
    PERMISSIONS[(locationMode !== "all" && locationForPermission) ? locationForPermission.id! : "all"].updateDrd = value;
  }

  function syncOrderPrint(value: boolean) {
    setOrderPrint(value);
    PERMISSIONS[(locationMode !== "all" && locationForPermission) ? locationForPermission.id! : "all"].orderPrint = value;
  }

  function syncHolidayTemplates(value: boolean) {
    setHolidayTemplates(value);
    PERMISSIONS[(locationMode !== "all" && locationForPermission) ? locationForPermission.id! : "all"].holidayTemplates = value;
  }

  function toggleLoc(id: string) {
    setSelLocations((prev) => {
      const newSel = prev.includes(id)
        ? prev.filter((item) => item !== id)
        : [...prev, id];

      if (!newSel.includes(id) && locationMode === "byLocation") {
        delete PERMISSIONS[id];
      }

      return newSel;
    });
  }

  function selectAll(checked: boolean) {
    if (checked) {
      setSelLocations(locations.map((loc) => loc.id!));
    } else {
      setSelLocations([]);
      resetPermissions();
    }
  }

  const allSelected = selLocations.length === locations.length;

  /*const hasDrdAccess = useMemo(() => {
    if (!member.diningRDIntegration) return false;
    if (locationMode === "all") {
      return selLocations.some((locId) => locations.find((l) => l.id === locId)?.drdAccess);
    }
    return !!locationForPermission?.drdAccess;
  }, [locationForPermission?.drdAccess, locationMode, locations, member.diningRDIntegration, selLocations]);*/

  // Access level is a single choice backed by mutually-exclusive flags. Item Library Only
  // and DiningRD "Data Only" are conditional variants that behave as extra levels.
  type RawLevel = "viewOnly" | "dataOnly" | "itemLibraryOnly" | "downloadPublish" | "updateDrd";

  const rawLevel: RawLevel | null =
    viewOnly ? "viewOnly" :
      itemLibraryOnly ? "itemLibraryOnly" :
        dataOnly ? "dataOnly" :
          downloadPublish ? "downloadPublish" :
            updateDrd ? "updateDrd" : null;

  function toAbstractLevel(raw: RawLevel | null): AccessLevel | null {
    if (raw === "viewOnly") return "viewOnly";
    if (raw === "downloadPublish") return "editDesigns";
    if (raw) return "editMenuData"; // dataOnly / itemLibraryOnly / updateDrd
    return null;
  }

  const abstractLevel = toAbstractLevel(rawLevel);

  const availableAddOns = addOnsFor(!!member.diningRDIntegration);

  const selectedAddOns: AddOn[] = [
    createTemplate && "createTemplates",
    publish && "publish",
    orderPrint && "orderPrint",
    holidayTemplates && "holidayTemplates",
  ].filter(Boolean) as AddOn[];

  function setLevel(raw: RawLevel) {
    syncViewOnly(raw === "viewOnly");
    syncItemLibraryOnly(raw === "itemLibraryOnly");
    syncDataOnly(raw === "dataOnly");
    syncDownloadAndPublish(raw === "downloadPublish");
    syncUpdateDrd(raw === "updateDrd");
    syncManageBrand(false);

    // Drop add-ons that don't apply to the new level.
    const keep = clearInvalidAddOns(toAbstractLevel(raw), selectedAddOns);
    syncCreateTemplates(keep.includes("createTemplates"));
    syncPublish(keep.includes("publish"));
    syncOrderPrint(keep.includes("orderPrint"));
    syncHolidayTemplates(keep.includes("holidayTemplates"));
  }

  function toggleAddOn(key: AddOn, checked: boolean) {
    if (key === "createTemplates") syncCreateTemplates(checked);
    if (key === "publish") syncPublish(checked);
    if (key === "orderPrint") syncOrderPrint(checked);
    if (key === "holidayTemplates") syncHolidayTemplates(checked);
  }

  const accessLevelOptions: { key: RawLevel; label: string; description: string; disabled?: boolean }[] = [
    {key: "viewOnly", label: "View Only", description: "Users can view and download existing designs but cannot edit or publish."},
    (member.showItemLibrary && !member.diningRDIntegration)
      ? {key: "itemLibraryOnly", label: "Edit Menu Data", description: "Users can update Item Library data in existing templates, make small spacing adjustments, and download the final output."}
      : {key: "dataOnly", label: "Edit Menu Data", description: "Users can update menu data on existing templates, make small spacing adjustments, and download the final output."},
    {key: "downloadPublish", label: "Edit Designs", description: "Full access to design and formatting tools in the editor. Users can edit designs, download files, and publish updates."},
  ];

  const selectedLevelOption = accessLevelOptions.find((o) => o.key === rawLevel);
  const summaryText = selectedLevelOption
    ? [selectedLevelOption.label, ...availableAddOns.filter((a) => isAddOnChecked(abstractLevel, a, selectedAddOns)).map((a) => a.label)].join(" · ")
    : "No access";

  function changeLocationMode(value: "all" | "byLocation") {
    setLocationMode(value);
    if (value === "byLocation") {
      let permLoc = locations[0];
      if (selLocations && selLocations.length > 0) {
        let foundSelLoc = locations.find(l => l.id === selLocations[0]);
        if (foundSelLoc) {
          permLoc = foundSelLoc;
        }
      }
      setLocationForPermission(permLoc);
      if (!PERMISSIONS[permLoc.id!]) {
        PERMISSIONS[permLoc.id!] = getDefaultLocationPermission(permLoc.internalId!);
      }
      updatePermissionsForLocation(PERMISSIONS[permLoc.id!]);
    } else {
      if (id) {
        recomputeAllPermissions(selLocations);
      }
      updatePermissionsForLocation(PERMISSIONS["all"]);
    }
  }

  function resetPermissions() {
    PERMISSIONS = {
      all: {
        viewOnly: false,
        downloadPublish: true,
        publish: false,
        createTemplate: true,
        updateDrd: false,
        orderPrint: true,
        manageBrand: false,
        itemLibraryOnly: false,
        dataOnly: false,
        holidayTemplates: false,
      }
    }
  }

  function backToLocations() {
    setStep("locations");
  }

  function preparePermissions() {
    if (locationMode === "all") {
      selLocations.forEach(it => {
        const found = locations.find((l) => l.id === it);
        PERMISSIONS[it] = {
          viewOnly: PERMISSIONS["all"].viewOnly,
          downloadPublish: PERMISSIONS["all"].downloadPublish,
          publish: PERMISSIONS["all"].publish,
          createTemplate: PERMISSIONS["all"].createTemplate,
          updateDrd: PERMISSIONS["all"].updateDrd,
          orderPrint: PERMISSIONS["all"].orderPrint,
          itemLibraryOnly: PERMISSIONS["all"].itemLibraryOnly,
          dataOnly: PERMISSIONS["all"].dataOnly,
          manageBrand: PERMISSIONS["all"].manageBrand,
          holidayTemplates: PERMISSIONS["all"].holidayTemplates,
          locationId: found?.internalId,
        }
      })
    } else {
      // For "byLocation" mode, ensure all selected locations have a PERMISSIONS entry
      selLocations.forEach(it => {
        if (!PERMISSIONS[it]) {
          const found = locations.find((l) => l.id === it);
          PERMISSIONS[it] = getDefaultLocationPermission(found?.internalId!);
        }
      })
    }
  }

  function getCleanPermissions() {
    preparePermissions();

    let cleanPermissions = {} as any;

    Object.keys(PERMISSIONS).forEach(key => {
      if (key === "all") return;
      const value = PERMISSIONS[key]
      if (value.viewOnly || value.downloadPublish || value.createTemplate || value.updateDrd || value.orderPrint || value.itemLibraryOnly || value.dataOnly || value.manageBrand) {
        cleanPermissions[key] = value;
      }
    });

    return cleanPermissions;
  }

  function updateSelectedUser() {
    const locs = getCleanPermissions();
    if (!locs || Object.keys(locs).length === 0) {
      return;
    }

    setSaving(true);
    MembersApi.updateUserLocationPermissions(id ?? "", {
      activate: !!activate,
      locations: locs
    }).then(() => {
      setSaving(false);
      success("Updated!")
      resetPermissions()
      navigate(env.dashboardPath + "/account/team");
    }).catch((err) => {
      setSaving(false);
      handleServerError(err);
    });
  }

  function inviteNewUser() {
    const locs = getCleanPermissions();
    if (role === "user" && (!locs || Object.keys(locs).length === 0)) {
      return;
    }

    setSaving(true);
    MembersApi.createNewUserInTeam({
      ownerId: member.id,
      role,
      email,
      locations: locs
    }).then(() => {
      setSaving(false);
      success("User invited!")
      resetPermissions()
      navigate(env.dashboardPath + "/account/team?pending");
    }).catch(err => {
      setSaving(false);
      handleServerError(err);
    });
  }

  function submit() {
    if (!hasSelectedPermissions) {
      error("At least one permission must be selected for this location.");
      return;
    }
    if (!id) {
      inviteNewUser();
    } else {
      updateSelectedUser();
    }
  }

  function confirmPermissions() {
    if (locationMode === "byLocation" && selLocations.length > 0) {
      const firstLoc = locations.find(l => l.id === selLocations[0]);
      if (firstLoc) {
        selectLocationForPermissions(firstLoc);
      }
    }
    setStep("permissions");
  }

  function onRoleChange(ev: RadioChangeEvent) {
    console.log(ev);
    setRole(ev.target.value)
  }

  return (
    <>
      {loading && <Skeleton/>}

      {!loading && step === "email" && (
        <div className={"new-user-step-mail"}>
          <Title level={2}>Add a new user</Title>
          <label>New User Email</label>
          <div className={"field"}>
            <Input
              placeholder="Email address"
              size={"large"}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="role-selection">
            <label> Role</label>
            <Radio.Group value={role} onChange={onRoleChange}>
              <Radio value="admin">
                <div>
                  <h6>Admin</h6>
                  <p>
                    Admins have full access to all features, user management, and all locations.<br/>NOTE: Only the
                    Account Owner can manage billing and subscriptions.
                  </p>
                </div>
              </Radio>
              <Radio value="user">
                <div>
                  <h6>User</h6>
                  <p>
                    Users have assigned permissions per location only.
                  </p>
                </div>
              </Radio>
            </Radio.Group>
          </div>

          <footer>
            <Button size={"large"} type="primary" onClick={handleEmailSubmit} loading={saving} disabled={!email}>
              {role === "admin" ? "Done" : "Next"}
            </Button>
          </footer>
        </div>
      )}

      {
        !loading && step === "locations" && (
          <div className={"new-user-step-locations"}>
            <header>
              <Button className={"back"} type={"link"} onClick={() => navigate(-1)}>
                <LeftOutlined/> Back
              </Button>
              <Title level={3}>Manage Location Assignment</Title>
              <p>Select location assignments for <b>{email}</b></p>
            </header>

            <ul>
              <li>
                <Checkbox checked={allSelected} onChange={(e) => selectAll(e.target.checked)}>
                  All Locations
                </Checkbox>
              </li>
              {locations.map((location) => (
                <li key={location.id}>
                  <Checkbox checked={selLocations.includes(location.id!)} onChange={() => toggleLoc(location.id!)}>
                    {location.name}
                  </Checkbox>
                </li>
              ))}
            </ul>
            <Button type="primary" size="large" disabled={selLocations.length === 0}
                    onClick={confirmPermissions}>
              Next
            </Button>
          </div>
        )}

      {!loading && step === "permissions" && (
        <div className={"new-user-step-permissions"}>
          <header>
            <Button className={"back"} type={"link"} onClick={backToLocations}>
              <LeftOutlined/>
              Manage Location Assignment
            </Button>
            {!id && <Title level={3}>Set Permissions</Title>}
            {!!id && <Title level={3}>Edit Permissions</Title>}
            <p>
              Select permissions for <b>{email}</b>
              <br/>
              Permissions are location specific. You can set different permissions for each user in each location.
            </p>
          </header>

          <div className={"work"}>
            <div className={"left"}>
              <Title level={5}>Locations</Title>
              <Radio.Group value={locationMode} onChange={(e) => changeLocationMode(e.target.value)}>
                <Radio value="all">All Selected Locations</Radio>
                <Radio value="byLocation">By Location</Radio>
              </Radio.Group>

              {locationMode === "byLocation" && <ul className={"perm-locs"}>
                {selLocations.map(it => {
                  const found = locations.find((l) => l.id === it);
                  return <li key={it} className={it === locationForPermission?.id ? "sel" : ""}
                             onClick={() => selectLocationForPermissions(found!)}>
                    {found?.name}
                  </li>
                })}
              </ul>}
            </div>

            <div className={"right"}>
              {!!locationForPermission && locationMode !== 'all' && <h6>{locationForPermission.name}</h6>}

              <div className={"perm-groups"}>
                <div className={"perm-col access-level"}>
                  <div className={"group-title"}>ACCESS LEVEL</div>
                  <Radio.Group value={rawLevel ?? undefined} onChange={(e) => setLevel(e.target.value)}>
                    {accessLevelOptions.map((opt) => (
                      <Radio key={opt.key} value={opt.key} disabled={opt.disabled}
                             className={`level-card${rawLevel === opt.key ? " selected" : ""}`}>
                        <span className={"label"}>{opt.label}</span>
                        <span className={"desc"}>{opt.description}</span>
                      </Radio>
                    ))}
                  </Radio.Group>
                </div>

                <div className={"perm-col add-ons"}>
                  <div className={"group-title"}>ADD ONS</div>
                  {availableAddOns.map((addOn) => {
                    const disabled = isAddOnDisabled(abstractLevel, addOn);
                    const note = addOnNote(abstractLevel, addOn);
                    return (
                      <label key={addOn.key} className={`addon-card${disabled ? " disabled" : ""}`}>
                        <Checkbox
                          checked={isAddOnChecked(abstractLevel, addOn, selectedAddOns)}
                          disabled={disabled}
                          onChange={(e) => toggleAddOn(addOn.key, e.target.checked)}
                        />
                        <span className={"text"}>
                          <span className={"label"}>{addOn.label}</span>
                          <span className={"desc"}>{addOn.description}</span>
                          {note && <span className={"note"}>{note}</span>}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className={"perm-summary"}>
                <span className={"muted"}>This user can: </span>
                <b>{summaryText}</b>
              </div>

              <div className={"warn-info"}>
                {!hasSelectedPermissions && <h5>
                  At least one permission must be selected for this location.
                </h5>}
              </div>
            </div>
          </div>

          <div className="next-btn-container">
            <Button type="primary" size="large" disabled={!hasSelectedPermissions} onClick={submit} loading={saving}>
              Done
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
